import { memo, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Recipe } from "@corpuscomplete/core/nutrition/recipe";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { useDraft } from "@corpuscomplete/web/hooks/use-draft";
import { FoodCollectionUI } from "@corpuscomplete/core-ui/nutrition/food-collection";
import { RecipeList } from "@corpuscomplete/core-ui/nutrition/recipe/list";
import { RecipeUI } from "@corpuscomplete/core-ui/nutrition/recipe";
import { NutrientAmountsLabel } from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@corpuscomplete/web-ui/components/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@corpuscomplete/web-ui/components/empty";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { BookOpenIcon, ChevronDownIcon } from "lucide-react";
import { useZeroVirtualizer, useHistoryScrollState } from "@rocicorp/zero-virtual/react";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { ScopeBreadcrumb } from "@corpuscomplete/web/modules/common/components/scope-breadcrumb";
import { Scope } from "@corpuscomplete/core/common/scope";
import { Link } from "@corpuscomplete/web/components/link";

/**
 * Recipes index route boundary.
 * Contract: nests RecipeList/RecipeForm core-ui providers and keeps
 * collection filtering plus modal orchestration in the web layer.
 */
export function formatDurationMinutes(minutes: number | null): string {
  return minutes === null ? "N/A" : `${minutes}mins`;
}

export function RecipesPage({ collectionId, scope }: { collectionId?: string; scope: Scope.Info }) {
  return <RecipesPageContent collectionId={collectionId} scope={scope} />;
}

function RecipesPageContent({ collectionId, scope }: { collectionId?: string; scope: Scope.Info }) {
  const navigate = useNavigate();
  const { userId } = useAuth();

  const changeScope = (nextScopeId: string) =>
    nextScopeId === userId
      ? void navigate({
          to: "/personal/recipes",
        })
      : void navigate({
          to: "/spaces/$spaceId/recipes",
          params: { spaceId: nextScopeId },
        });

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <div className="mr-auto min-w-0">
            <ScopeBreadcrumb
              crumbs={[{ label: "Recipes", current: true }]}
              onScopeChange={changeScope}
              scope={scope}
              scopeBehavior="switchable"
            />
          </div>
          <CollectionFilter collectionId={collectionId} scope={scope} />
          {collectionId ? <CreateRecipeButton collectionId={collectionId} scope={scope} /> : null}
        </PageHeader>
        <PageContent>
          {collectionId ? (
            <RecipeTable collectionId={collectionId} scope={scope} />
          ) : (
            <NoRecipeCollectionState scope={scope} />
          )}
        </PageContent>
      </PageBody>
    </Page>
  );
}

function NoRecipeCollectionState({ scope }: { scope: Scope.Info }) {
  return (
    <Empty className="flex-1">
      <EmptyHeader>
        <EmptyMedia>
          <div className="flex size-16 items-center justify-center rounded-full bg-muted">
            <BookOpenIcon className="size-8 text-muted-foreground" />
          </div>
        </EmptyMedia>
        <EmptyTitle>No recipe collection in this scope</EmptyTitle>
        <EmptyDescription>
          Create a food collection for this scope before adding recipes.
        </EmptyDescription>
      </EmptyHeader>
      <Button
        render={
          scope.kind === "personal" ? (
            <Link to="/personal/collections" />
          ) : (
            <Link to="/spaces/$spaceId/collections" params={{ spaceId: scope.spaceId }} />
          )
        }
      >
        Manage Collections
      </Button>
    </Empty>
  );
}

export function CollectionFilter({
  collectionId,
  scope,
}: {
  collectionId?: string;
  scope: Scope.Info;
}) {
  const navigate = useNavigate();
  const scopeId = Scope.id(scope);
  const collections = FoodCollectionUI.useForContentManagement({ foodType: "recipe" }).filter(
    (collection) => collection.scopeId === scopeId,
  );
  const selectedCollection = collectionId
    ? collections.find((c) => c.id === collectionId)
    : undefined;

  if (collections.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="outline" size="xs" />}>
          <Text variant="xs">{selectedCollection?.name ?? "Select Collection"}</Text>
          <ChevronDownIcon className="ml-2 size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {collections.map((collection) => (
            <DropdownMenuItem
              key={collection.id}
              onClick={() =>
                void navigate({
                  to: scope.kind === "personal" ? "/personal/recipes" : "/spaces/$spaceId/recipes",
                  params: scope.kind === "personal" ? {} : { spaceId: scope.spaceId },
                  search: { collectionId: collection.id },
                })
              }
            >
              <Text variant="xs">{collection.name}</Text>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function estimateSize() {
  return 44;
}

function RecipeTable({ collectionId, scope }: { collectionId: string; scope: Scope.Info }) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useHistoryScrollState<Recipe.ListCursor>("recipe-list");

  const { virtualizer, rowAt, complete, rowsEmpty } = useZeroVirtualizer({
    ...RecipeList.useVirtualArgs(collectionId),
    estimateSize,
    getScrollElement: () => parentRef.current,
    scrollState,
    onScrollStateChange: setScrollState,
  });

  const virtualItems = virtualizer.getVirtualItems();

  if (complete && rowsEmpty) {
    return (
      <Empty className="flex-1">
        <EmptyHeader>
          <EmptyMedia>
            <div className="flex size-16 items-center justify-center rounded-full bg-muted">
              <BookOpenIcon className="size-8 text-muted-foreground" />
            </div>
          </EmptyMedia>
          <EmptyTitle>No recipes yet</EmptyTitle>
          <EmptyDescription>
            Create your first recipe to start building your cookbook.
          </EmptyDescription>
        </EmptyHeader>
        <CreateRecipeButton collectionId={collectionId} scope={scope} />
      </Empty>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-y border-border/70 bg-background">
      <div ref={parentRef} className="min-h-0 flex-1 overflow-y-auto">
        <div
          id="recipes-table"
          className="relative"
          style={{
            height: virtualizer.getTotalSize(),
          }}
        >
          {virtualItems.map((item) => {
            const recipe = rowAt(item.index);

            return (
              <div
                key={item.key}
                className="absolute left-0 top-0 w-full"
                style={{
                  transform: `translateY(${item.start}px)`,
                }}
              >
                <RecipeCard recipe={recipe} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const RecipeCard = memo(({ recipe }: { recipe: Recipe.ListItem | undefined }) => {
  const navigate = useNavigate();
  const { archive } = RecipeUI.useMutations();

  if (!recipe?.serving?.base || !recipe?.base) {
    return (
      <div className="flex h-11 items-center justify-between gap-4 border-b border-border/60 px-4 py-1 transition-colors hover:bg-muted/40" />
    );
  }

  const amounts = Nutrient.addFoodItems(Nutrient.emptyAmounts(), [
    { amount: 1, serving: recipe.serving.base },
  ]);

  return (
    <ContextMenu>
      <ContextMenuTrigger
        render={
          <Link
            to="/recipes/$id"
            params={{ id: recipe.id }}
            className="flex h-11 items-center justify-between gap-4 border-b border-border/60 px-4 py-1 transition-colors hover:bg-muted/40"
          />
        }
      >
        <div className="min-w-0">
          <Text variant="xs" className="truncate font-medium text-foreground">
            {recipe.base.name}
          </Text>
        </div>
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          <Text variant="xs" className="truncate text-muted-foreground">
            {recipe.serving.perRecipe} {recipe.serving.base.name}
          </Text>
          <Text variant="xs" className="text-muted-foreground/60">
            ·
          </Text>
          <NutrientAmountsLabel
            amounts={amounts}
            abbreviated
            className="min-w-0 flex-nowrap gap-1 overflow-hidden"
          />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onClick={() =>
            void navigate({
              to: "/recipes/$id/edit",
              params: { id: recipe.id },
            })
          }
        >
          <Text variant="sm">Edit</Text>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          variant="destructive"
          onClick={() => archive(recipe.id, recipe.base?.meta?.currentVersion ?? 0)}
        >
          <Text variant="sm">Delete</Text>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
});

function CreateRecipeButton({
  className,
  collectionId,
  scope,
}: {
  className?: string;
  collectionId?: string;
  scope: Scope.Info;
}) {
  const { exists: draftExists } = useDraft("recipe.create", Recipe.formSchema("storage"), () =>
    Recipe.formDefault(undefined),
  );

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className={className}
        render={
          scope.kind === "personal" ? (
            <Link to="/personal/recipes/create" search={{ initialCollectionId: collectionId }} />
          ) : (
            <Link
              to="/spaces/$spaceId/recipes/create"
              params={{ spaceId: scope.spaceId }}
              search={{ initialCollectionId: collectionId }}
            />
          )
        }
      >
        Create Recipe
      </Button>
      {draftExists && (
        <div
          className="absolute top-[-2px] right-[-2px] h-2 w-2 rounded-full bg-red-500 shadow-sm"
          style={{ opacity: draftExists ? 1 : 0, transition: "opacity 100ms" }}
        />
      )}
    </div>
  );
}
